import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { AlertModule } from 'ngx-bootstrap/alert';
import { TranslocoModule } from '@jsverse/transloco';
import { finalize } from 'rxjs';
import { AvailableLanguage, LanguageService } from '../../language';
import { LoaderGlobeAnimationDetailComponent, LoadingService } from '../../loaders';
import { UserSettingsService } from '../../user-settings';

@Component({
  selector: 'multisite-account-notifications',
  templateUrl: './account-notifications.component.html',
  styleUrls: ['./account-notifications.component.scss'],
  standalone: true,
  imports: [
    RouterModule,
    TranslocoModule,
    AlertModule,
    LoaderGlobeAnimationDetailComponent,
  ],
})
export class AccountNotificationsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadingService = inject(LoadingService);
  private readonly router = inject(Router);

  /** The reader setting that holds back notifications emails; absent means they are sent. */
  private static readonly NOTIFICATION_EMAILS = 'notification_emails';

  readonly availableLanguages = signal<AvailableLanguage[]>([]);
  readonly selectedNotificationLanguage = signal<string | null>(null);
  readonly savedNotificationLanguage = signal<string | null>(null);
  readonly loadingObject = signal<{ [key: string]: boolean }>({});
  readonly initialLoading = signal(true);
  readonly error = signal<string | null>(null);
  readonly showSavedFeedback = signal(false);
  readonly notificationEmails = signal(true);

  /** Only an application with ideas can list the ones the reader follows. */
  readonly hasIdeas = this.router.config.some((route) => route.path === 'ideas');

  readonly isAnythingLoading = computed(() => {
    return this.loadingService.isAnythingLoading(this.loadingObject())
      || this.loadingService.isAnythingLoading(this.userSettingsService.loadingObjectSignal());
  });

  constructor(
    private readonly languageService: LanguageService,
    private readonly userSettingsService: UserSettingsService,
  ) {
    this.availableLanguages.set(this.languageService.availableLanguages ?? []);
  }

  ngOnInit(): void {
    this.loadPreferredLanguage();
    this.loadNotificationEmails();
  }

  onNotificationEmailsChanged(on: boolean): void {
    if (this.isAnythingLoading()) {
      return;
    }

    this.error.set(null);
    this.showSavedFeedback.set(false);
    this.notificationEmails.set(on);
    this.setLoadingStatus('notificationEmails', true);

    this.userSettingsService
      .upsertSetting(AccountNotificationsComponent.NOTIFICATION_EMAILS, on ? '1' : '0')
      .pipe(finalize(() => this.setLoadingStatus('notificationEmails', false)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedValue) => {
          this.notificationEmails.set(savedValue !== '0');
          this.showSavedFeedback.set(true);
          setTimeout(() => this.showSavedFeedback.set(false), 2500);
        },
        error: (errorResponse) => {
          this.notificationEmails.set(!on);
          this.error.set(typeof errorResponse === 'string' ? errorResponse : (errorResponse?.message ?? 'error.something_went_wrong'));
        },
      });
  }

  private loadNotificationEmails(): void {
    this.setLoadingStatus('notificationEmails', true);

    this.userSettingsService
      .getSetting(AccountNotificationsComponent.NOTIFICATION_EMAILS)
      .pipe(finalize(() => this.setLoadingStatus('notificationEmails', false)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (value) => this.notificationEmails.set(value !== '0'),
        error: () => this.notificationEmails.set(true),
      });
  }

  onLanguageSelected(languageKey: string): void {
    if (this.isAnythingLoading() || !languageKey) {
      return;
    }

    const currentlySaved = this.savedNotificationLanguage();
    if (currentlySaved === languageKey) {
      return;
    }

    this.error.set(null);
    this.showSavedFeedback.set(false);
    this.selectedNotificationLanguage.set(languageKey);
    this.setLoadingStatus('autosave', true);

    this.userSettingsService
      .upsertSetting('preferred_lang', languageKey)
      .pipe(
        finalize(() => {
          this.setLoadingStatus('autosave', false);
        }),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedValue) => {
          this.savedNotificationLanguage.set(savedValue);
          this.selectedNotificationLanguage.set(savedValue);
          this.showSavedFeedback.set(true);
          setTimeout(() => this.showSavedFeedback.set(false), 2500);
        },
        error: (errorResponse) => {
          this.selectedNotificationLanguage.set(this.savedNotificationLanguage());
          this.error.set(typeof errorResponse === 'string' ? errorResponse : (errorResponse?.message ?? 'error.something_went_wrong'));
        },
      });
  }

  private loadPreferredLanguage(): void {
    this.error.set(null);
    this.setLoadingStatus('preferredLanguage', true);

    this.userSettingsService
      .getSetting('preferred_lang')
      .pipe(
        finalize(() => {
          this.initialLoading.set(false);
          this.setLoadingStatus('preferredLanguage', false);
        }),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preferredLanguage) => {
          this.savedNotificationLanguage.set(preferredLanguage);
          this.selectedNotificationLanguage.set(preferredLanguage);
        },
        error: (errorResponse) => {
          this.error.set(typeof errorResponse === 'string' ? errorResponse : (errorResponse?.message ?? 'error.something_went_wrong'));
        },
      });
  }

  private setLoadingStatus(key: string, status: boolean): void {
    this.loadingObject.set(this.loadingService.setLoadingStatus(key, status, this.loadingObject()));
  }
}
